import { useMemo, useState } from 'react';

import type { CategoryDto } from '@entities/product';
import { haptics } from '@shared/lib/telegram';
import { AnimatedTabs, type AnimatedTabItem } from '@shared/ui/AnimatedTabs';
import styles from './StoreSectionSwitcher.module.css';

const SERVICES_SEEN_KEY = 'yeneshop:services-tab-seen';
const ALL = 'all';
const SERVICES = 'services';

function hasSeenServices(): boolean {
  try {
    return localStorage.getItem(SERVICES_SEEN_KEY) === '1';
  } catch {
    // If the WebView cannot persist the flag, avoid showing "new" forever.
    return true;
  }
}

interface StoreSectionSwitcherProps {
  categories: CategoryDto[];
  servicesOffered: boolean;
  showingServices: boolean;
  categoryId: string | null;
  onSelectServices: () => void;
  onSelectCategory: (categoryId: string | null) => void;
}

/** Store-specific labels and persistence around the shared tab primitive. */
export function StoreSectionSwitcher({
  categories,
  servicesOffered,
  showingServices,
  categoryId,
  onSelectServices,
  onSelectCategory,
}: StoreSectionSwitcherProps) {
  const [servicesSeen, setServicesSeen] = useState(() => hasSeenServices());
  const items = useMemo<AnimatedTabItem[]>(
    () => [
      { id: ALL, label: 'All' },
      ...(servicesOffered
        ? [
            {
              id: SERVICES,
              label: (
                <>
                  Services
                  {!servicesSeen ? <span className={styles.newDot} aria-label="New" /> : null}
                </>
              ),
            },
          ]
        : []),
      ...categories.map((category) => ({ id: category.id, label: category.name })),
    ],
    [categories, servicesOffered, servicesSeen],
  );

  if (!servicesOffered && categories.length === 0) return null;

  return (
    <AnimatedTabs
      className={styles.switcher}
      ariaLabel="Product sections"
      value={showingServices ? SERVICES : (categoryId ?? ALL)}
      items={items}
      onSelectionFeedback={haptics.select}
      onChange={(next) => {
        if (next === SERVICES) {
          setServicesSeen(true);
          try {
            localStorage.setItem(SERVICES_SEEN_KEY, '1');
          } catch {
            // The badge is still cleared for this mounted session.
          }
          onSelectServices();
          return;
        }

        onSelectCategory(next === ALL ? null : next);
      }}
    />
  );
}
